'use client';
import React from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import Toggle from '@/components/ui/Toggle';
import { TutorConfig } from './AITutorBuilderContent';

interface Props {
  config: TutorConfig;
  updateConfig: (u: Partial<TutorConfig>) => void;
  onNext: () => void;
  onBack: () => void;
}

const explanationStyles = [
  { id: 'analogy', label: 'Analogy-First', desc: 'Explains new concepts using familiar real-world comparisons' },
  { id: 'step-by-step', label: 'Step-by-Step', desc: 'Breaks every concept into small, sequential numbered steps' },
  { id: 'visual', label: 'Visual Description', desc: 'Uses diagrams, charts, and spatial descriptions to teach' },
  { id: 'example-first', label: 'Example-First', desc: 'Shows a working example before explaining the theory' },
];

const summaryFrequencies = [
  { id: 'end-of-lesson', label: 'End of Each Lesson' },
  { id: 'end-of-module', label: 'End of Each Module' },
  { id: 'on-request', label: 'Only When Asked' },
  { id: 'always', label: 'After Every Concept' },
];

export default function TutorBuilderStep3({ config, updateConfig, onNext, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-1">Learning Behavior</h2>
        <p className="text-sm text-muted-foreground">Define how your tutor explains concepts, summarizes lessons, and stores learning memory.</p>
      </div>

      {/* Explanation style */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Explanation Style</label>
        <p className="text-xs text-muted-foreground mb-3">How should your tutor introduce new concepts?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {explanationStyles.map((s) => (
            <button
              key={`explain-${s.id}`}
              type="button"
              onClick={() => updateConfig({ explanationStyle: s.id })}
              className={`p-4 rounded-xl border-2 text-left transition-all btn-scale ${
                config.explanationStyle === s.id
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-muted-foreground/30'
              }`}
            >
              <p className="text-sm font-bold text-foreground mb-1">{s.label}</p>
              <p className="text-xs text-muted-foreground">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Summary frequency */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">Summary Frequency</label>
        <p className="text-xs text-muted-foreground mb-3">How often should the tutor recap what was learned?</p>
        <div className="flex flex-wrap gap-2">
          {summaryFrequencies.map((f) => (
            <button
              key={`summary-${f.id}`}
              type="button"
              onClick={() => updateConfig({ summaryFrequency: f.id })}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all btn-scale ${
                config.summaryFrequency === f.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Memory */}
      <div className="space-y-4 p-4 bg-muted/40 rounded-xl border border-border">
        <div>
          <p className="text-sm font-semibold text-foreground mb-0.5">Learning Memory</p>
          <p className="text-xs text-muted-foreground">Store summaries of what the student has learned as a personal memory timeline.</p>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Enable Learning Memory</p>
              <p className="text-xs text-muted-foreground">Saves lesson summaries, key concepts, and tutor interactions</p>
            </div>
            <Toggle
              checked={config.memoryEnabled}
              onChange={(v) => updateConfig({ memoryEnabled: v })}
            />
          </div>
        </div>
        {config.memoryEnabled && (
          <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-xs text-primary font-medium">
              ✓ Memory enabled — your tutor will summarize each lesson and store it in your Learning Memory Timeline.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors"
        >
          Continue to Safety <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}